const createTextSegment = (textKey, options = {}) => ({
  textKey,
  strong: false,
  danger: false,
  ...options,
})

const createTextItem = (textKey) => ({ textKey })

const createSegmentItem = (segments) => ({ segments })

const createTitleBlock = (textKey) => ({
  type: "title",
  textKey,
})

const createParagraphBlock = (textKey) => ({
  type: "paragraph",
  textKey,
})

const createImageBlock = (imageKey) => ({
  type: "image",
  imageKey,
})

const createRichTextBlock = (segments) => ({
  type: "richText",
  segments,
})

const createListBlock = (titleKey, items) => ({
  type: "list",
  titleKey,
  items,
})

const normalizeSegments = (segments) => segments.map((segment) => createTextSegment(
  segment.textKey,
  { strong: segment.strong, danger: segment.danger },
))

const normalizeListItems = (items) => items.map((item) => {
  if (item.textKey) {
    return createTextItem(item.textKey)
  }

  return createSegmentItem(normalizeSegments(item.segments))
})

const normalizeBlocks = (scopeId, blocks) => Object.freeze(
  blocks.map((block, index) => {
    const normalizedBlock = {
      ...block,
      id: `${scopeId}-${block.type}-${index + 1}`,
    }

    if (block.type === "richText") {
      normalizedBlock.segments = normalizeSegments(block.segments)
    }

    if (block.type === "list") {
      normalizedBlock.items = normalizeListItems(block.items)
    }

    return Object.freeze(normalizedBlock)
  }),
)

const createSection = ({ id, titleKey, defaultExpanded = true, blocks }) => Object.freeze({
  id,
  titleKey,
  defaultExpanded,
  blocks: normalizeBlocks(id, blocks),
})

export const settingGuideImageMap = Object.freeze({
  bindFormPreview: "image1",
  authCodeExample: "image2",
  serverLookupExample: "image3",
  serverLookupForm: "image4",
  neteaseLogin: "image5",
  neteaseProtocolSettings: "image6",
  neteaseAuthPopup: "image7",
  neteaseAuthList: "image8",
  neteaseBindForm: "image9",
  qqLogin: "image10",
  qqSecuritySettings: "image11",
  qqAuthPopup: "image12",
  qqAuthList: "image13",
  qqBindForm: "image14",
  aliyunConsole: "image15",
  aliyunMailSettings: "image16",
  aliyunSecuritySettings: "image17",
  aliyunPasswordList: "image18",
  googleAccountSecurity: "image19",
  googleAppPasswordsEntry: "image20",
  googleAppPasswordsDirectLink: "image21",
  googleAppPasswordResult: "image22",
})

export const settingGuideIntroBlocks = normalizeBlocks("intro", [
  createTitleBlock("new.guide.message.title"),
  createParagraphBlock("new.text.1"),
  createParagraphBlock("new.text.2"),
  createParagraphBlock("new.text.3"),
  createImageBlock("bindFormPreview"),
  createRichTextBlock([
    createTextSegment("new.text.w.1", { strong: true }),
    createTextSegment("new.text.n.1"),
  ]),
  createImageBlock("authCodeExample"),
  createRichTextBlock([
    createTextSegment("new.text.w.2", { strong: true }),
    createTextSegment("new.text.n.2"),
  ]),
  createImageBlock("serverLookupExample"),
  createImageBlock("serverLookupForm"),
  createParagraphBlock("new.show.txt"),
])

export const settingGuideSections = Object.freeze([
  createSection({
    id: "part1",
    titleKey: "new.guide.part.1.title",
    blocks: [
      createImageBlock("neteaseLogin"),
      createParagraphBlock("new.wy.t.1"),
      createImageBlock("neteaseProtocolSettings"),
      createRichTextBlock([
        createTextSegment("new.wy.t.2-1"),
        createTextSegment("new.wy.t.2-2", { strong: true }),
        createTextSegment("new.wy.t.2-3"),
        createTextSegment("new.wy.t.2-4", { strong: true }),
        createTextSegment("new.wy.t.2-5"),
        createTextSegment("new.wy.t.2-6", { strong: true }),
      ]),
      createImageBlock("neteaseAuthPopup"),
      createListBlock("new.wy.t.3", [
        createTextItem("new.wy.t.4"),
        createTextItem("new.wy.t.5"),
        createTextItem("new.wy.t.6"),
        createSegmentItem([
          createTextSegment("new.wy.t.7-1"),
          createTextSegment("new.wy.t.7-2", { strong: true, danger: true }),
          createTextSegment("new.wy.t.7-3"),
        ]),
      ]),
      createImageBlock("neteaseAuthList"),
      createParagraphBlock("new.wy.t.8"),
      createImageBlock("neteaseBindForm"),
      createParagraphBlock("new.wy.t.9"),
    ],
  }),
  createSection({
    id: "part2",
    titleKey: "new.guide.part.2.title",
    blocks: [
      createImageBlock("qqLogin"),
      createParagraphBlock("new.qq.t.1"),
      createImageBlock("qqSecuritySettings"),
      createParagraphBlock("new.qq.t.2"),
      createImageBlock("qqAuthPopup"),
      createListBlock("new.wy.t.3", [
        createTextItem("new.qq.t.3"),
        createTextItem("new.qq.t.4"),
        createSegmentItem([
          createTextSegment("new.qq.t.5-1"),
          createTextSegment("new.qq.t.5-2", { strong: true, danger: true }),
          createTextSegment("new.qq.t.5-3"),
        ]),
      ]),
      createImageBlock("qqAuthList"),
      createParagraphBlock("new.qq.t.6"),
      createImageBlock("qqBindForm"),
      createParagraphBlock("new.qq.t.7"),
    ],
  }),
  createSection({
    id: "part3",
    titleKey: "new.guide.part.3.title",
    blocks: [
      createImageBlock("aliyunConsole"),
      createImageBlock("aliyunMailSettings"),
      createRichTextBlock([
        createTextSegment("new.al.t.1"),
        createTextSegment("new.al.t.1-2", { strong: true }),
        createTextSegment("new.al.t.1-3"),
        createTextSegment("new.al.t.1-4", { strong: true }),
        createTextSegment("new.al.t.1-5"),
      ]),
      createImageBlock("aliyunSecuritySettings"),
      createParagraphBlock("new.al.t.2"),
      createImageBlock("aliyunPasswordList"),
      createRichTextBlock([
        createTextSegment("new.al.t.3-1"),
        createTextSegment("new.al.t.3-2", { strong: true }),
        createTextSegment("new.al.t.3-3"),
      ]),
      createRichTextBlock([
        createTextSegment("new.al.t.4-1"),
        createTextSegment("new.al.t.4-2", { strong: true, danger: true }),
        createTextSegment("new.al.t.4-3"),
      ]),
    ],
  }),
  createSection({
    id: "part4",
    titleKey: "new.guide.part.4.title",
    blocks: [
      createImageBlock("googleAccountSecurity"),
      createParagraphBlock("new.gl.t.1"),
      createImageBlock("googleAppPasswordsEntry"),
      createRichTextBlock([
        createTextSegment("new.gl.t.2-1"),
        createTextSegment("new.gl.t.2-2", { strong: true }),
        createTextSegment("new.gl.t.2-3"),
      ]),
      createImageBlock("googleAppPasswordsDirectLink"),
      createParagraphBlock("new.gl.t.3"),
      createImageBlock("googleAppPasswordResult"),
      createParagraphBlock("new.gl.t.4"),
      createParagraphBlock("new.gl.t.5"),
    ],
  }),
  createSection({
    id: "part5",
    titleKey: "new.guide.part.5.title",
    blocks: [
      createParagraphBlock("new.end.txt"),
    ],
  }),
])
